import React from "react";
import { Link } from "react-router-dom";
import { MarketingLayout } from "@/marketing/MarketingLayout";
import { Seo } from "@/components/Seo";
import { SectionLabel, IDButton, Monogram, TAGLINE } from "@/marketing/brand";
import { SignupForm } from "@/marketing/SignupForm";
import { BookOpen, HeartPulse, Briefcase, Coins, Users, ArrowRight, Mail, Compass, Sparkles } from "lucide-react";

/*
 * / — Intrinsic Daily home (PUBLIC brand landing).
 * Purpose: funnel visitors to the two live experiences — the Sunday Reset
 * newsletter (PRIMARY CTA) and the free Life Audit — with a tasteful glimpse of
 * the Life OS being built. Reuses MarketingLayout (header/footer) + brand system.
 */

const PILLARS = [
  { key: "disciple", name: "The Disciple", domain: "Faith & Devotion", icon: BookOpen },
  { key: "keeper", name: "The Keeper", domain: "Health & Vitality", icon: HeartPulse },
  { key: "builder", name: "The Builder", domain: "Work & Calling", icon: Briefcase },
  { key: "steward", name: "The Steward", domain: "Wealth & Provision", icon: Coins },
  { key: "anchor", name: "The Anchor", domain: "Relationships & Legacy", icon: Users },
];

const Wrap = ({ className = "", children }) => (
  <div className={`mx-auto max-w-[1280px] px-5 sm:px-8 ${className}`}>{children}</div>
);

export default function Home() {
  return (
    <MarketingLayout>
      <Seo slug="home" />
      <div data-testid="home-page">

        {/* ---------------- Hero ---------------- */}
        <Wrap className="pt-14 pb-16 sm:pt-20 lg:pt-24 lg:pb-24">
          <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
            <div className="text-center lg:text-left">
              <SectionLabel className="!tracking-[0.3em]">Intrinsic Daily · for the Christian woman</SectionLabel>
              <h1 className="id-serif mt-6 text-[2.2rem] leading-[1.06] text-[var(--id-charcoal)] sm:text-5xl lg:text-[4.25rem] lg:leading-[1.04]">
                Grow with intention. Live each day on purpose.
              </h1>
              <p className="id-sans mx-auto mt-5 max-w-[560px] text-[16.5px] leading-relaxed text-[var(--id-charcoal)]/70 sm:text-[18px] lg:mx-0">
                A faith-centered way to tend your whole life — faith, health, work, wealth, and
                relationships — with grace instead of hustle. It begins with one quiet Sunday letter.
              </p>
              {/* Inline hero signup — one-tap capture, no click-through to another page. */}
              <div className="mt-8 flex flex-col items-center gap-4 lg:items-start">
                <div className="mx-auto w-full max-w-[520px] lg:mx-0">
                  <SignupForm
                    variant="hero"
                    showName={false}
                    source="home"
                    testidPrefix="home-hero-signup"
                    cta="Subscribe"
                    microcopy="One quiet Sunday letter. Free. Grace over shame. Unsubscribe anytime."
                  />
                </div>
                <Link
                  to="/life-audit"
                  data-testid="home-hero-audit"
                  className="id-sans inline-flex items-center gap-1.5 text-[14px] font-medium text-[var(--id-charcoal)]/70 underline decoration-[var(--id-gold)]/40 underline-offset-4 transition-colors hover:text-[var(--id-charcoal)]">
                  Or take the free Life Audit <ArrowRight size={15} />
                </Link>
              </div>
            </div>
            {/* Right — brand statement card */}
            <div className="rounded-[28px] border border-[color:rgba(32,32,32,0.10)] bg-white px-8 py-11 shadow-[0_44px_90px_-45px_rgba(43,38,34,0.45)] sm:px-11 sm:py-12">
              <div className="flex items-center justify-between">
                <span className="id-sans text-[11px] font-semibold uppercase tracking-[0.24em] text-[var(--id-gold)]">Our belief</span>
                <Monogram className="h-9" />
              </div>
              <p className="id-serif mt-9 text-[2rem] leading-[1.26] text-[var(--id-charcoal)] sm:text-[2.15rem]">
                You don't need to become someone new. You need space to live as the woman God has already
                called you to be.
              </p>
              <div className="mt-9 h-px w-full bg-[color:rgba(32,32,32,0.10)]" />
              <p className="id-serif mt-6 text-xl text-[var(--id-walnut)]">{TAGLINE}</p>
            </div>
          </div>
        </Wrap>

        {/* ---------------- Belief / recognition ---------------- */}
        <Wrap className="py-16 sm:py-20">
          <div className="rounded-3xl border border-[color:rgba(32,32,32,0.10)] bg-[var(--id-ivory)] px-6 py-14 text-center sm:px-14 sm:py-16">
            <div className="mx-auto max-w-[820px]">
              <SectionLabel>Grace over hustle</SectionLabel>
              <p className="id-serif mt-6 text-[1.9rem] leading-relaxed text-[var(--id-charcoal)] sm:text-[2.25rem]">
                Faith, home, work, health, and the people you love aren't five competing to-do lists.
                They're one life — meant to be tended with intention, not held together by pressure.
              </p>
              <p className="id-sans mx-auto mt-6 max-w-[600px] text-[16px] leading-relaxed text-[var(--id-charcoal)]/65">
                Intrinsic Daily exists to help you live that one life on purpose — beginning with small,
                faithful steps you can actually keep.
              </p>
            </div>
          </div>
        </Wrap>

        {/* ---------------- Two ways to begin (funnel) ---------------- */}
        <Wrap className="py-16 sm:py-20">
          <div className="max-w-[640px]">
            <SectionLabel>Two gentle ways to begin</SectionLabel>
            <h2 className="id-serif mt-4 text-[2.1rem] text-[var(--id-charcoal)] sm:text-[2.75rem]">Start where you are — both are free</h2>
          </div>
          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            {/* Sunday Reset — primary */}
            <div className="flex flex-col rounded-3xl border border-[color:rgba(32,32,32,0.10)] bg-white px-8 py-10 sm:px-10">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--id-gold)]/12 text-[var(--id-gold)]">
                <Mail size={22} strokeWidth={1.7} />
              </span>
              <h3 className="id-serif mt-6 text-3xl text-[var(--id-charcoal)]">The Sunday Reset</h3>
              <p className="id-sans mt-1 text-[13px] font-semibold uppercase tracking-[0.16em] text-[var(--id-gold)]">A free weekly letter</p>
              <p className="id-sans mt-4 flex-1 text-[16px] leading-relaxed text-[var(--id-charcoal)]/65">
                Ten unhurried minutes each Sunday — one Scripture to center on, one gentle reset, and one
                pillar to tend — so your faith and your week finally speak to each other.
              </p>
              <div className="mt-7">
                <IDButton as="link" to="/sunday-reset" variant="solid" testid="home-card-subscribe" className="!px-7">
                  Subscribe free <ArrowRight size={16} />
                </IDButton>
              </div>
            </div>
            {/* Life Audit */}
            <div className="flex flex-col rounded-3xl border border-[color:rgba(32,32,32,0.10)] bg-white px-8 py-10 sm:px-10">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--id-gold)]/12 text-[var(--id-gold)]">
                <Compass size={22} strokeWidth={1.7} />
              </span>
              <h3 className="id-serif mt-6 text-3xl text-[var(--id-charcoal)]">The Life Audit</h3>
              <p className="id-sans mt-1 text-[13px] font-semibold uppercase tracking-[0.16em] text-[var(--id-gold)]">A free 10-minute reflection</p>
              <p className="id-sans mt-4 flex-1 text-[16px] leading-relaxed text-[var(--id-charcoal)]/65">
                One honest look across your five life pillars. See what is steady, what needs tending, and
                the one faithful next step that could bring meaningful relief this season.
              </p>
              <div className="mt-7">
                <IDButton as="link" to="/life-audit" variant="ghost" testid="home-card-audit" className="!px-7">
                  Take the Life Audit <ArrowRight size={16} />
                </IDButton>
              </div>
            </div>
          </div>
        </Wrap>

        {/* ---------------- Five Life Pillars ---------------- */}
        <Wrap className="py-16 sm:py-20">
          <div className="mx-auto max-w-[640px] text-center">
            <SectionLabel>The five callings you carry</SectionLabel>
            <h2 className="id-serif mt-4 text-[2.1rem] text-[var(--id-charcoal)] sm:text-[2.75rem]">Five Life Pillars, one whole life</h2>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
            {PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.key} className="rounded-2xl border border-[color:rgba(32,32,32,0.10)] bg-white px-6 py-8 text-center">
                  <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--id-gold)]/12 text-[var(--id-gold)]">
                    <Icon size={22} strokeWidth={1.7} />
                  </span>
                  <h3 className="id-serif mt-4 text-2xl text-[var(--id-charcoal)]">{p.name}</h3>
                  <p className="id-sans mt-1 text-[13.5px] text-[var(--id-charcoal)]/55">{p.domain}</p>
                </div>
              );
            })}
          </div>
        </Wrap>

        {/* ---------------- What we're building (Life OS vision) ---------------- */}
        <Wrap className="py-16 sm:py-20">
          <div className="overflow-hidden rounded-3xl bg-[var(--id-walnut)] text-[var(--id-ivory)]">
            <div className="grid gap-10 px-7 py-14 sm:px-14 sm:py-16 lg:grid-cols-[0.95fr_1.05fr] lg:items-center lg:gap-16">
              <div>
                <span className="id-sans inline-flex items-center gap-2 rounded-full bg-white/10 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--id-gold)]">
                  <Sparkles size={13} /> What we're building
                </span>
                <h2 className="id-serif mt-6 text-[2.1rem] leading-tight sm:text-[2.75rem]">A gentle operating system for a whole, faithful life.</h2>
                <p className="id-sans mt-5 max-w-[520px] text-[16px] leading-relaxed text-[var(--id-ivory)]/75">
                  The Sunday Reset and the Life Audit are the front porch. We're quietly building Intrinsic
                  Daily — a calm home where your vision, seasons, and days come together, so intention
                  becomes a rhythm you can actually live.
                </p>
                <p className="id-sans mt-6 text-[13px] uppercase tracking-[0.18em] text-[var(--id-ivory)]/45">
                  In the making · subscribe to follow along
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  ["From vision to today", "Carry your calling from a yearly vision down to a single, doable day."],
                  ["Five pillars, in balance", "Tend faith, health, work, wealth, and relationships as one life."],
                  ["Grace-filled reflection", "Weekly rhythms of honest review and gentle course-correction."],
                  ["A snapshot, never a verdict", "Progress measured with compassion — room to rest and begin again."],
                ].map(([t, d]) => (
                  <div key={t} className="rounded-2xl bg-white/5 px-5 py-6">
                    <h3 className="id-serif text-xl text-[var(--id-ivory)]">{t}</h3>
                    <p className="id-sans mt-2 text-[14px] leading-relaxed text-[var(--id-ivory)]/70">{d}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Wrap>

        {/* ---------------- Primary CTA ---------------- */}
        <Wrap className="pb-24 pt-4 sm:pb-28">
          <div className="rounded-3xl bg-[var(--id-charcoal)] px-6 py-16 text-center sm:px-14 sm:py-20">
            <SectionLabel>Begin this Sunday</SectionLabel>
            <h2 className="id-serif mx-auto mt-5 max-w-[720px] text-[2.4rem] leading-tight text-[var(--id-ivory)] sm:text-5xl">
              One quiet letter. One faithful step at a time.
            </h2>
            <p className="id-sans mx-auto mt-5 max-w-[520px] text-[16px] leading-relaxed text-[var(--id-ivory)]/65">
              Join the Christian women resetting with grace and intention every Sunday.
            </p>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <IDButton as="link" to="/sunday-reset" variant="gold" testid="home-final-subscribe" className="!px-9 !py-4 text-[15px]">
                Subscribe to the Sunday Reset <ArrowRight size={17} />
              </IDButton>
              <IDButton as="link" to="/life-audit" variant="ghostLight" testid="home-final-audit" className="!px-7 !py-4 text-[15px]">
                Or take the Free Life Audit
              </IDButton>
            </div>
          </div>
        </Wrap>

      </div>
    </MarketingLayout>
  );
}
